import React, { useEffect, useReducer } from 'react';
import { clearCollisionDebugger } from '../../../debug/CollisionDebugger';
import {
  CollisionFailure,
  clearCollisionFailures,
  getCollisionFailures,
  getCollisionMonitorStatus,
} from '../../../debug/CollisionSafetyMonitor';

/**
 * PhysicsSafetyOverlayContent — kit tool `physicsSafety`. Collision-failure
 * monitor reading the module-level CollisionSafetyMonitor ring (1Hz refresh) +
 * a clear button. Recreated into the 🛠 kit during the dev-tools modernization
 * arc (2026-05-29). Player physics-state + per-second stats were dropped —
 * Position Tracker / Physics Diag cover live physics state; this tool is the
 * failure log, which needs no props.
 *
 * DEBUG-SAFETY-WIRE-01 (2026-07-27): this window used to render an empty ring
 * as "✅ No collision failures detected". The ring has no producer —
 * `CollisionSafetyMonitor.monitorCharacter`'s only caller was the Phaser-era
 * `PhysicsSystem`, deleted in 97d7e42b — so that green tick was reporting the
 * absence of a measurement as the success of one. It now refuses to give a
 * verdict it does not have, the same call the aggro overlay made when it drew
 * "unknown" instead of a plausible constant.
 */
export const PhysicsSafetyOverlayContent: React.FC = () => {
  const [, refresh] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    const id = setInterval(refresh, 1000);
    return () => clearInterval(id);
  }, []);

  const failures = getCollisionFailures();
  const status = getCollisionMonitorStatus();

  return (
    <div style={{ padding: 10, fontSize: 11, color: '#ccc', overflow: 'auto', height: '100%' }}>
      <h3 style={{ color: '#e74c3c', margin: '0 0 8px' }}>⚠️ Collision Safety Monitor</h3>
      <div style={{ maxHeight: 220, overflowY: 'auto', marginBottom: 12, border: '1px solid rgba(231,76,60,0.3)', borderRadius: 4, padding: 5 }}>
        {!status.wired ? (
          // No producer ⇒ no verdict. Amber, never green: an unmeasured system
          // is not a healthy one, and this window is read as a health signal.
          <div style={{ color: '#f1c40f', padding: 10, textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold' }}>⚠️ NOT WIRED — no data</div>
            <div style={{ marginTop: 4, fontSize: 10, color: '#aaa' }}>
              Nothing is calling <code>CollisionSafetyMonitor.monitorCharacter()</code>,
              so no character has been measured. The empty buffer below is the
              absence of a measurement, <strong>not</strong> a clean result.
            </div>
          </div>
        ) : failures.length === 0 ? (
          <div style={{ color: '#2ecc71', padding: 10, textAlign: 'center' }}>
            ✅ No collision failures observed
            <div style={{ marginTop: 4, fontSize: 10, color: '#888' }}>
              across {status.observations} character observation(s)
            </div>
          </div>
        ) : (
          failures.map((failure: CollisionFailure, i: number) => (
            <div key={`failure-${i}`} style={{
              padding: 6,
              marginBottom: 3,
              background: failure.severity === 'error' ? 'rgba(231,76,60,0.15)' : 'rgba(241,196,15,0.15)',
              borderLeft: `3px solid ${failure.severity === 'error' ? '#e74c3c' : '#f1c40f'}`,
              fontSize: 10,
              borderRadius: 2,
            }}>
              <strong style={{ color: failure.severity === 'error' ? '#e74c3c' : '#f1c40f' }}>{failure.type}</strong>
              <div style={{ marginTop: 3, color: '#ccc' }}>{failure.message}</div>
              <div style={{ marginTop: 2, fontSize: 8, color: '#888' }}>
                pos: ({Math.round(failure.position?.x || 0)}, {Math.round(failure.position?.y || 0)}) ·
                vel: ({failure.velocity?.x?.toFixed(1) || 0}, {failure.velocity?.y?.toFixed(1) || 0})
              </div>
            </div>
          ))
        )}
      </div>
      <div style={{ marginBottom: 4 }}>
        {failures.length} failure(s) in buffer · {status.observations} observation(s)
      </div>
      <button
        onClick={() => { clearCollisionDebugger(); clearCollisionFailures(); refresh(); }}
        style={{ background: '#e74c3c', border: 'none', color: 'white', padding: '6px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 10 }}
      >
        🗑️ Clear Buffer
      </button>
    </div>
  );
};
